import { PostRagChat } from '../redux/dataroomSlice';
import { resolveIntelligenceAbstention } from './accuracyModeMeta';
import {
    buildIntelligenceAskBody,
    isIntelligenceAskEnabled,
    normalizeIntelligenceAskResponse,
    postIntelligenceAsk,
} from '../redux/intelligenceSlice';
import { isInsightsModuleAskOrigin } from './intelligenceInsightsAskLensOrigin';

/** Intelligence router returned list_report stub with no retrieval. */
export function isIntelligenceListReportRedirect(data) {
    const rd = data?.retrieval_debug || {};
    const intent = String(rd.router_intent || rd.intent || '').toLowerCase();
    if (intent === 'vdr_catalog') return false;
    if (intent === 'list_report') return true;
    const answer = String(data?.final_answer || data?.answer || '').toLowerCase();
    return (
        answer.includes('retrieve_all_chunks') &&
        answer.includes('rag_chat')
    );
}

/** Deterministic Document-metadata catalog answer (no chunk RAG). */
export function isIntelligenceVdrCatalogResponse(data) {
    const rd = data?.retrieval_debug || {};
    const intent = String(rd.router_intent || rd.intent || '').toLowerCase();
    if (intent === 'vdr_catalog') return true;
    return Boolean(rd?.vdr_catalog && typeof rd.vdr_catalog === 'object');
}

/** Structured DB / canonical tool answer (no chunk RAG). */
export function isIntelligenceDbToolResponse(data) {
    const rd = data?.retrieval_debug || {};
    const intent = String(rd.router_intent || rd.intent || '').toLowerCase();
    if (intent === 'db_tools') return true;
    const mode = String(rd.router_mode || '').toLowerCase();
    if (['db', 'canonical', 'clarify'].includes(mode)) return true;
    return (
        Array.isArray(rd.answer_provenance) &&
        rd.answer_provenance.length > 0 &&
        !rd.db_tool_hybrid_merge
    );
}

function isIrlSourceType(sourceType) {
    const st = String(sourceType || '').toLowerCase();
    return st === 'irl' || st === 'irl_row';
}

function isVdrSourceType(sourceType) {
    const st = String(sourceType || '').toLowerCase();
    return st === 'vdr_chunk' || st === 'vdr';
}

function isObservationSourceType(sourceType, meta = {}) {
    const st = String(sourceType || '').toLowerCase();
    return st === 'observation' || meta?.observation_id != null;
}

/**
 * True when intelligence returned usable VDR or IRL material (not blocked empty synthesis).
 */
export function hasUsableVdrEvidence(data) {
    if (!data) return false;
    if (isIntelligenceVdrCatalogResponse(data)) return true;
    if (isIntelligenceDbToolResponse(data)) return true;
    if (isIntelligenceListReportRedirect(data)) return false;

    const rd = data.retrieval_debug || {};
    const vdrHits = Number(rd.vdr_hit_count);
    const irlHits = Number(rd.irl_hit_count);
    const obsHits = Number(rd.observation_hit_count);
    const fused = Number(rd.fused_count);
    const evidence = Array.isArray(data.evidence) ? data.evidence : [];
    const citations = Array.isArray(data.citations) ? data.citations : [];
    const chunks = Array.isArray(data.chunks) ? data.chunks : [];

    const vdrEvidence = evidence.filter(ev => isVdrSourceType(ev?.source_type));
    const irlEvidence = evidence.filter(
        ev =>
            isIrlSourceType(ev?.source_type) ||
            (ev?.metadata?.irl_id != null && !isObservationSourceType(ev?.source_type, ev?.metadata)),
    );
    const obsEvidence = evidence.filter(
        ev => isObservationSourceType(ev?.source_type, ev?.metadata),
    );

    const gov = data.evidence_governance || rd.grounding_status || '';
    const grounding = String(
        typeof gov === 'object' ? gov.grounding_status || gov.status : gov,
    ).toLowerCase();
    if (grounding === 'blocked_no_evidence') return false;

    const answer = String(data.final_answer || data.answer || '').toLowerCase();
    const looksEmpty =
        answer.includes('insufficient evidence') &&
        (Number.isFinite(vdrHits) ? vdrHits === 0 : true) &&
        (Number.isFinite(irlHits) ? irlHits === 0 : true) &&
        (Number.isFinite(obsHits) ? obsHits === 0 : true) &&
        vdrEvidence.length === 0 &&
        irlEvidence.length === 0 &&
        obsEvidence.length === 0 &&
        citations.length === 0 &&
        chunks.length === 0;

    if (looksEmpty) return false;

    if (Number.isFinite(vdrHits) && vdrHits > 0) return true;
    if (Number.isFinite(irlHits) && irlHits > 0) return true;
    if (Number.isFinite(obsHits) && obsHits > 0) return true;
    if (
        Number.isFinite(fused) &&
        fused > 0 &&
        (vdrEvidence.length > 0 || irlEvidence.length > 0 || obsEvidence.length > 0)
    ) {
        return true;
    }
    if (vdrEvidence.length > 0 || irlEvidence.length > 0 || obsEvidence.length > 0) return true;

    const vdrChunks = chunks.filter(ch => {
        const st = String(ch?.source_type || '').toLowerCase();
        return (isVdrSourceType(st) || ch?.file_id) && !ch?.irl_id;
    });
    if (vdrChunks.length > 0) return true;

    const irlChunks = chunks.filter(
        ch => isIrlSourceType(ch?.source_type) || ch?.irl_id != null || ch?.irl_ref,
    );
    if (irlChunks.length > 0) return true;

    const vdrCitations = citations.filter(c => isVdrSourceType(c?.source_type) || c?.file_id);
    if (vdrCitations.length > 0) return true;

    const irlCitations = citations.filter(
        c =>
            isIrlSourceType(c?.source_type) ||
            ((c?.irl_id != null || c?.irl_ref) && !isObservationSourceType(c?.source_type, c)),
    );
    if (irlCitations.length > 0) return true;

    const obsChunks = chunks.filter(
        ch => isObservationSourceType(ch?.source_type, ch) || ch?.observation_id != null,
    );
    if (obsChunks.length > 0) return true;

    const obsCitations = citations.filter(c => isObservationSourceType(c?.source_type, c));
    return obsCitations.length > 0;
}

/** Keep intelligence abstention answers; do not replace with VDR-only rag_chat. */
export function isIntelligenceAbstentionResponse(data) {
    if (!data) return false;
    return resolveIntelligenceAbstention({
        answer: data.final_answer || data.answer,
        retrievalDebug: data.retrieval_debug,
        evidenceGovernance: data.evidence_governance,
    }).isAbstention;
}

/** Intelligence returned a structured answer — never fall back to legacy rag_chat. */
export function isIntelligenceStructuredResponse(data) {
    if (!data) return false;
    if (isIntelligenceVdrCatalogResponse(data)) return true;
    if (isIntelligenceDbToolResponse(data)) return true;
    if (isIntelligenceListReportRedirect(data)) return false;
    const rd = data.retrieval_debug || {};
    if (rd.mixed_planner_required) return true;
    if (String(rd.router_mode || '').toLowerCase() === 'insights_module_report') return true;
    if (String(rd.router_mode || '').toLowerCase() === 'tool_planner') return true;
    if (data?.irl_claim_validation?.support_verdict) return true;
    return false;
}

/**
 * Prefer intelligence ask; fall back to legacy rag_chat when VDR retrieval is empty.
 */
export async function dispatchScopedRagAsk({
    dispatch,
    projectId,
    wsId,
    scope,
    body,
    /** Shorter question for rag_chat when intelligence returns no VDR hits (e.g. IRL validation). */
    ragFallbackBody,
    preferIntelligence = true,
    accuracyMode = false,
    conversationId = null,
    persistConversation = false,
    insightsOrigin = null,
}) {
    const anchorWsId = Number(scope?.workstreamIds?.[0]);
    if (!Number.isFinite(anchorWsId)) {
        throw new Error('No workstream in scope.');
    }

    const useIntelligence =
        preferIntelligence && isIntelligenceAskEnabled() && !body?.retrieve_all_chunks;

    let intelligenceConversationId = null;

    const moduleOnlyAsk = isInsightsModuleAskOrigin(insightsOrigin);
    const effectiveScope = moduleOnlyAsk
        ? {
              ...scope,
              includeVdr: false,
              includeIrl: false,
              includeObservations: false,
          }
        : scope;

    if (useIntelligence) {
        try {
            const intelBody = buildIntelligenceAskBody(
                { question: body.question },
                effectiveScope,
                wsId,
                {
                    includeVdr: effectiveScope.includeVdr,
                    includeIrl: effectiveScope.includeIrl,
                    includeObservations: effectiveScope.includeObservations,
                    irlTags: scope.irlTags,
                    observationTags: scope.observationTags,
                    accuracyMode: accuracyMode === true,
                    conversationId: conversationId || undefined,
                    persistConversation: persistConversation === true || Boolean(conversationId),
                    insightsOrigin: insightsOrigin || undefined,
                },
            );
            const data = await dispatch(
                postIntelligenceAsk({
                    projectId,
                    body: intelBody,
                }),
            ).unwrap();

            intelligenceConversationId = data?.conversation_id || null;

            const isIrlClaimValidation =
                Number.isFinite(Number(intelBody?.primary_irl_id)) ||
                Boolean(data?.irl_claim_validation?.support_verdict);

            if (
                hasUsableVdrEvidence(data) ||
                isIntelligenceStructuredResponse(data) ||
                isIrlClaimValidation ||
                isIntelligenceAbstentionResponse(data) ||
                effectiveScope.includeVdr === false
            ) {
                return {
                    data,
                    usedIntelligenceAsk: true,
                    intelligenceMeta: {
                        trace_id: data.trace_id,
                        confidence: data.confidence,
                        retrieval_debug: data.retrieval_debug,
                        evidence_governance: data.evidence_governance,
                        alignment_analysis: data.alignment_analysis,
                        irl_claim_validation: data.irl_claim_validation,
                        observation_candidates: data.observation_candidates,
                    },
                    usedRagFallback: false,
                };
            }
        } catch (err) {
            // Normalization or network failure — legacy rag_chat is VDR-only.
            if (typeof console !== 'undefined' && console.warn) {
                console.warn('[intelligence ask] failed; falling back to rag_chat (VDR only)', err);
            }
        }
    }

    const fallbackBody = ragFallbackBody || body;
    const ragData = await dispatch(
        PostRagChat({
            projectId,
            wsId: anchorWsId,
            body: fallbackBody,
        }),
    ).unwrap();

    const ragChunks = Array.isArray(ragData.chunks) ? ragData.chunks : [];
    const normalized = normalizeIntelligenceAskResponse({
        question: fallbackBody.question,
        answer: ragData.final_answer || ragData.answer || '',
        citations: ragData.citations || [],
        evidence: ragData.evidence || [],
        confidence: ragData.confidence || 'medium',
        conversation_id: intelligenceConversationId,
        retrieval_debug: {
            ...(ragData.retrieval_debug || {}),
            rag_chat_fallback: true,
            vdr_hit_count: ragChunks.filter(ch => ch?.file_id).length,
        },
    });
    if (!normalized.chunks?.length && ragChunks.length) {
        normalized.chunks = ragChunks;
    }

    return {
        data: normalized,
        usedIntelligenceAsk: false,
        intelligenceMeta: {
            trace_id: ragData.trace_id ?? null,
            confidence: normalized.confidence,
            retrieval_debug: normalized.retrieval_debug,
        },
        usedRagFallback: true,
    };
}
